import { Outlet } from "react-router-dom";

import posthog from "posthog-js";
import { ReactNode } from "react";
import { useBookingFunnelCMSPrefetch } from "~/hooks/use-booking-funnel-cms-prefetch";
import { isEmbedded } from "~/lib/embed";

type CustomErrorBoundaryProps = {
    children: ReactNode;
    fallback: (error: Error | null, errorInfo: React.ErrorInfo | null) => ReactNode;
};

import React from "react";

type CustomErrorBoundaryState = {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
};

const CustomErrorBoundary = (props: CustomErrorBoundaryProps) => {
    class ErrorBoundary extends React.Component<CustomErrorBoundaryProps, CustomErrorBoundaryState> {
        constructor(props: CustomErrorBoundaryProps) {
            super(props);
            this.state = { hasError: false, error: null, errorInfo: null };
        }

        static getDerivedStateFromError(error: Error): CustomErrorBoundaryState {
            return { hasError: true, error, errorInfo: null };
        }

        componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
            this.setState({ errorInfo });
            posthog.captureException?.(error);
        }

        render() {
            if (this.state.hasError) {
                return this.props.fallback(this.state.error, this.state.errorInfo);
            }
            return this.props.children;
        }
    }

    return <ErrorBoundary {...props} />;
}

// Component that handles CMS prefetching
const CMSPrefetchProvider = ({ children }: { children: ReactNode }) => {
    useBookingFunnelCMSPrefetch();
    return <>{children}</>;
};

const BaseLayout = () => {
    return (
        <CMSPrefetchProvider>
            <CustomErrorBoundary fallback={(error, errorInfo) => (
                <div className='w-full flex flex-col items-center justify-center gap-4 py-16 text-dark font-matter font-normal leading-none md:max-w-7xl md:mx-auto'>
                    <span className="text-lg font-medium">Etwas ist schiefgelaufen.</span>

                    {/* Error details section */}
                    <div className="w-full max-w-4xl bg-red-50 border border-red-200 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-red-800 mb-2">Error Details:</h3>
                        {error && (
                            <div className="mb-3">
                                <p className="text-sm text-red-700 font-medium">Message:</p>
                                <p className="text-sm text-red-600 bg-white p-2 rounded border">{error.message}</p>
                            </div>
                        )}
                        {error?.stack && (
                            <div className="mb-3">
                                <p className="text-sm text-red-700 font-medium">Stack Trace:</p>
                                <pre className="text-xs text-red-600 bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap">
                                    {error.stack}
                                </pre>
                            </div>
                        )}
                        {errorInfo?.componentStack && (
                            <div>
                                <p className="text-sm text-red-700 font-medium">Component Stack:</p>
                                <pre className="text-xs text-red-600 bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap">
                                    {errorInfo.componentStack}
                                </pre>
                            </div>
                        )}
                    </div>

                    <a
                        href={isEmbedded() ? "/" : "https://kalialab.de/termin-buchen"}
                        className="inline-block px-6 py-2 bg-primary-600 text-white rounded-lg shadow hover:bg-primary-700 transition-colors"
                    >
                        Erneut versuchen
                    </a>
                    <span className="text-sm text-gray-500 text-center">
                        Oder besuchen Sie direkt&nbsp;
                        <a
                            href="https://kalialab.de/termin-buchen"
                            className="underline text-primary-600 hover:text-primary-700"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://kalialab.de/termin-buchen
                        </a>
                    </span>
                </div>
            )}>
                <div className='w-full h-full bg-light-burgundy'>
                    <div className='w-full flex flex-col gap-0 text-dark font-matter font-normal leading-none md:max-w-7xl md:mx-auto'>

                        <Outlet />
                    </div>
                </div>
            </CustomErrorBoundary>
        </CMSPrefetchProvider>
    );
};

export default BaseLayout;